import type { KycFileCertificationChoiceDto } from '@dealstack/shared/lib/generated';
import { uploadFileV2 } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	getProofOfAddress,
	getProofOfIdentity,
	resetInheritedKycItems,
	updateProofOfAddress,
	updateProofOfIdentity,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { KycResetScope } from '@dealstack/shared/lib/common/business/kyc';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useFileUploadErrorHandler } from '@dealstack/frontend/investor/hooks/useFileUploadErrorHandler';
import { investmentJoinAndAllocationContext } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/InvestmentJoinAndAllocation';
import { useKycFileConfirmation } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/useKycFileConfirmation';
import {
	hasUnconfirmedKycFiles,
	isKycRejected,
} from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	noSave,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import { useContext, useEffect } from 'react';

export const useJoinAndAllocationPersonalIDsController = () => {
	const tInvestor = useTranslationInvestor();
	const handleUploadError = useFileUploadErrorHandler();

	const { setIsContinueDisabled, investmentDetails, kycSetting } = useContext(
		investmentJoinAndAllocationContext
	);

	const kycWorkflowId = investmentDetails?.activeKycWorkflowId ?? '';

	const proofOfIdentityQuery = useQuery({
		queryKey: ['kyc', 'proofOfIdentity', kycWorkflowId],
		queryFn: () => getProofOfIdentity(kycWorkflowId),
		enabled: !!kycWorkflowId,
	});
	const uploadProofOfIdentityMutation = useMutation({
		mutationFn: uploadFileV2,
		onError: handleUploadError,
	});
	const updateProofOfIdentityMutation = useMutation({
		mutationFn: (variables: Parameters<typeof updateProofOfIdentity>[0]) =>
			updateProofOfIdentity(variables),
	});

	const onDropProofOfIdentity = (file: File, certification?: KycFileCertificationChoiceDto) => {
		uploadProofOfIdentityMutation.mutate(file, {
			onSuccess: ({ payload }) => {
				if (!payload) {
					return;
				}
				updateProofOfIdentityMutation.mutate(
					{ fileId: payload.id, kycWorkflowId, certification },
					{
						onSuccess: () => {
							showToastV2({ title: tInvestor('investment.toast.proof_uploaded') });
							void proofOfIdentityQuery.refetch();
						},
					}
				);
			},
		});
	};

	const onDeleteProofOfIdentity = () => {
		updateProofOfIdentityMutation.mutate(
			{ kycWorkflowId },
			{
				onSuccess: () => {
					showToastV2({ title: tInvestor('investment.toast.proof_removed') });
					void proofOfIdentityQuery.refetch();
				},
			}
		);
	};

	const proofOfAddressQuery = useQuery({
		queryKey: ['kyc', 'proofOfAddress', kycWorkflowId],
		queryFn: () => getProofOfAddress(kycWorkflowId),
		enabled: !!kycWorkflowId,
	});
	const uploadProofOfAddressMutation = useMutation({
		mutationFn: uploadFileV2,
		onError: handleUploadError,
	});
	const updateProofOfAddressMutation = useMutation({
		mutationFn: (variables: Parameters<typeof updateProofOfAddress>[0]) =>
			updateProofOfAddress(variables),
	});

	const onDropProofOfAddress = (file: File, certification?: KycFileCertificationChoiceDto) => {
		uploadProofOfAddressMutation.mutate(file, {
			onSuccess: ({ payload }) => {
				if (!payload) {
					return;
				}
				updateProofOfAddressMutation.mutate(
					{ fileId: payload.id, kycWorkflowId, certification },
					{
						onSuccess: () => {
							showToastV2({ title: tInvestor('investment.toast.proof_uploaded') });
							void proofOfAddressQuery.refetch();
						},
					}
				);
			},
		});
	};

	const onDeleteProofOfAddress = () => {
		updateProofOfAddressMutation.mutate(
			{ kycWorkflowId },
			{
				onSuccess: () => {
					showToastV2({ title: tInvestor('investment.toast.proof_removed') });
					void proofOfAddressQuery.refetch();
				},
			}
		);
	};

	const { createConfirmHandler } = useKycFileConfirmation();

	const onConfirmProofOfIdentity = createConfirmHandler(
		proofOfIdentityQuery.data?.payload?.id,
		() => void proofOfIdentityQuery.refetch()
	);

	const onConfirmProofOfAddress = createConfirmHandler(
		proofOfAddressQuery.data?.payload?.id,
		() => void proofOfAddressQuery.refetch()
	);

	const proofOfIdentityDetails = proofOfIdentityQuery.data?.payload;
	const proofOfAddressDetails = proofOfAddressQuery.data?.payload;

	const refetchAll = () => {
		void proofOfIdentityQuery.refetch();
		void proofOfAddressQuery.refetch();
	};

	const onResetAll = async () => {
		await resetInheritedKycItems(kycWorkflowId, KycResetScope.PROOF);
		refetchAll();
	};

	const isProofOfIdentityRequired = kycSetting?.isProofOfIdentityRequired ?? true;
	const isProofOfAddressRequired = kycSetting?.isProofOfAddressRequired ?? true;

	useEffect(() => {
		const isAllUploaded =
			(!isProofOfIdentityRequired || !!proofOfIdentityDetails) &&
			(!isProofOfAddressRequired || !!proofOfAddressDetails);
		const isNotRejected =
			!isKycRejected(proofOfIdentityDetails?.status) &&
			!isKycRejected(proofOfAddressDetails?.status);
		const hasUnconfirmed = hasUnconfirmedKycFiles([
			proofOfIdentityDetails,
			proofOfAddressDetails,
		]);

		setIsContinueDisabled(!isAllUploaded || !isNotRejected || hasUnconfirmed);
	}, [
		proofOfIdentityDetails,
		proofOfAddressDetails,
		isProofOfIdentityRequired,
		isProofOfAddressRequired,
	]);

	useWizardStep(noSave);

	return {
		uploadProofOfAddressRequest: uploadProofOfAddressMutation,
		uploadProofOfIdentityRequest: uploadProofOfIdentityMutation,
		isLoading: proofOfIdentityQuery.isLoading || proofOfAddressQuery.isLoading,
		proofOfIdentityDetails,
		proofOfAddressDetails,
		investmentDetails,
		kycSetting,
		onDropProofOfIdentity,
		onDeleteProofOfIdentity,
		onConfirmProofOfIdentity,
		onDropProofOfAddress,
		onDeleteProofOfAddress,
		onConfirmProofOfAddress,
		onResetAll,
		refetchAll,
	};
};
